
import React, { Suspense, useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { ScreenOrientation } from "@capacitor/screen-orientation";
import { installFullscreenOrientationListener } from "@/utils/videoOrientation";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { FastingProvider } from "@/contexts/FastingContext";
import { WaterTrackingProvider } from "@/contexts/WaterTrackingContext";
import { StepsProvider } from "@/contexts/StepsContext";
import { ChallengeProvider } from "@/contexts/ChallengeContext";
import { ImpersonationProvider } from "@/contexts/ImpersonationContext";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { AppUpdateGate } from "@/components/AppUpdateGate";
import { FastingNotificationManager } from "@/components/fasting/FastingNotificationManager";
import { PushNotificationInitializer } from "@/components/PushNotificationInitializer";
import { NavigateRefSetter } from "@/components/NavigateRefSetter";
import EntryPopupHost from "@/components/popups/EntryPopupHost";
// DEV-only visual harness for the entry popup; gated below so it never ships to prod.
const PopupPreviewDev = import.meta.env.DEV ? React.lazy(() => import("@/components/popups/PopupPreviewDev")) : null;
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminRoute from "@/components/AdminRoute";
import Layout from "@/components/Layout";
import ChallengeAccessGate from "@/components/challenge/ChallengeAccessGate";
import { METRIC_HISTORY_PATH, PROGRESS_PATH } from "@/lib/metricHistoryRoutes";
import { PERSONAL_PLAN_PATH } from "@/lib/personalPlan";


import ErrorBoundary from "@/components/ErrorBoundary";
import RouteErrorBoundary from "@/components/RouteErrorBoundary";
import { ScreenTrackingProvider } from "@/components/ScreenTrackingProvider";
import PendingReferralRedirect from "@/components/PendingReferralRedirect";

import { PostHogProvider } from "@posthog/react";
import { posthog } from "@/integrations/posthog/client";

// Lazy loaded pages
import {
  PageLoader,
  Home, Index, Library, Profile, Chat, Events, Settings,
  Recipe, CourseView, LessonView, ChallengeVideo, EventDetails,
  WaterTracking, Fasting, Steps, Subscribe, ProgressPage, MetricHistoryPage,
  AnalyticsPage, AudienceManagement, PostsManagement, Moderation,
  OnboardingManagement, FunnelManagement, CourseProductsManagement, ChallengeManagement, ChallengeEditor, SubscriptionBannersManager, AdminEntryPopups,
  CourseEditorPage, FastingNotificationsManagement, NotificationsManagement,
  LoginPage, OAuthConsent, OnboardingStepPage, NotFoundPage, PrivacyPolicy, TermsOfUse, Support, DeleteAccount,
  FunnelStepPage, FunnelDiagnosisPage, FunnelPaywallPage, FunnelSignupPage, FunnelCompletePage, PersonalPlanPage,
  ReferFriends, InviteLanding, RedeemReferral
} from "@/components/LazyPages";


// Home chunk is preloaded by main.tsx's Promise.all so it downloads in parallel
// with App; no need to call Home.preload() here.

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,  // 5 דקות - הנתונים נחשבים טריים
      gcTime: 30 * 60 * 1000,   // 30 דקות - זמן שמירה ב-cache
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => {
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      ScreenOrientation.lock({ orientation: 'portrait' }).catch(() => {});
    }
    // Allow rotation only while a video (or any element) is fullscreen.
    return installFullscreenOrientationListener();
  }, []);

  // רשת ביטחון גלובלית -- מונע קריסת WebView ממסך לבן
  useEffect(() => {
    const handleRejection = (event: PromiseRejectionEvent) => {
      console.error("[App] Unhandled rejection:", event.reason);
      event.preventDefault();
    };
    window.addEventListener("unhandledrejection", handleRejection);
    return () => window.removeEventListener("unhandledrejection", handleRejection);
  }, []);

  return (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Sonner />
        <BrowserRouter>
          <NavigateRefSetter />
          <PostHogProvider client={posthog}>
          <AuthProvider>
            <ImpersonationProvider>
              <ImpersonationBanner />
              <AppUpdateGate />
              <ChallengeProvider>
                <EntryPopupHost />
                <FastingProvider>
                <FastingNotificationManager />
                <PushNotificationInitializer />
                <WaterTrackingProvider>
                  <StepsProvider>
            <ScreenTrackingProvider />
            {/* מחזיר חבר/ה שהגיעו מקישור הזמנה אל /invite/<code> אחרי ההרשמה
                שהקישור התחיל. מרנדר null; כל השיקול ב-decidePendingReferralRedirect
                (טהור ונבדק), ואינו פעיל כשהמתג referrals_enabled כבוי. */}
            <PendingReferralRedirect />

            <Routes>
              {/* נתיבים ציבוריים — ללא Layout, ללא הגנת אימות */}
              <Route element={
                <Suspense fallback={<PageLoader />}>
                  <Outlet />
                </Suspense>
              }>
                <Route path="/login" element={<LoginPage.Component />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy.Component />} />
                <Route path="/terms-of-use" element={<TermsOfUse.Component />} />
                <Route path="/support" element={<Support.Component />} />
                <Route path="/delete-account" element={<DeleteAccount.Component />} />
                <Route path="/.lovable/oauth/consent" element={<OAuthConsent.Component />} />
                <Route path="/onboarding/:stepIndex" element={<OnboardingStepPage.Component />} />
                {/* קישור ההזמנה של חבר/ה: open.stayyoung.live/invite/<code> נוחת כאן —
                    באפליקציה (App Links / AASA) או בדפדפן. ציבורי בכוונה וללא שער
                    דגל ברמת הנתיב (R5): הדף מציג "התוכנית סגורה כרגע" כשהמתג כבוי,
                    ושומר את הקוד לפני ההרשמה — הפניה ל-/login כאן הייתה מאבדת אותו. */}
                <Route path="/invite/:code" element={<InviteLanding.Component />} />
                {/* משפך רכישה ציבורי — לפני יצירת חשבון. מוסתר מאחורי דגל
                    funnel_enabled_<platform> שברירת המחדל שלו כבויה, ולכן אף
                    משתמש לא מגיע לכאן עד שמפעילים אותו במפורש. הנתיב הספציפי
                    חייב להופיע לפני נתיב הפרמטר, אחרת "diagnosis" ייקלט כאינדקס */}
                <Route path="/start/diagnosis" element={<FunnelDiagnosisPage.Component />} />
                <Route path="/start/plan" element={<FunnelPaywallPage.Component />} />
                <Route path="/start/signup" element={<FunnelSignupPage.Component />} />
                <Route path="/start/complete" element={<FunnelCompletePage.Component />} />
                <Route path="/start" element={<FunnelStepPage.Component />} />
                <Route path="/start/:stepIndex" element={<FunnelStepPage.Component />} />
                {PopupPreviewDev && (
                  <Route path="/__popup-preview" element={<PopupPreviewDev />} />
                )}
              </Route>

              {/* נתיבים מוגנים ללא Layout — מאוחדים תחת parent יחיד כדי שמעבר ביניהם
                  לא יפיל את Suspense; ProtectedRoute עובר ל-element של כל ילד */}
              <Route element={
                <Suspense fallback={<PageLoader />}>
                  <Outlet />
                </Suspense>
              }>
                <Route path="/course/:id" element={<ProtectedRoute><RouteErrorBoundary><ChallengeAccessGate paramName="id"><CourseView.Component /></ChallengeAccessGate></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/course/:id/lesson/:lessonNumber" element={<ProtectedRoute><RouteErrorBoundary><ChallengeAccessGate paramName="id"><LessonView.Component /></ChallengeAccessGate></RouteErrorBoundary></ProtectedRoute>} />

                <Route path="/water-tracking" element={<ProtectedRoute><RouteErrorBoundary><WaterTracking.Component /></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/fasting" element={<ProtectedRoute><RouteErrorBoundary><Fasting.Component /></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/steps" element={<ProtectedRoute><RouteErrorBoundary><Steps.Component /></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/subscribe" element={<ProtectedRoute><RouteErrorBoundary><Subscribe.Component /></RouteErrorBoundary></ProtectedRoute>} />
                {/* הזמינו חברים — קישור ההזמנה האישי של המנוי. ProtectedRoute בלבד, בלי
                    Layout (מסך מלא עם חץ חזרה, כמו RedeemReferral). המסך עצמו מטפל
                    במתג referrals_enabled ובמצב התחזות — אין שער ברמת הנתיב (R5). */}
                <Route path="/refer" element={<ProtectedRoute><RouteErrorBoundary><ReferFriends.Component /></RouteErrorBoundary></ProtectedRoute>} />
                {/* "יש לי קוד הזמנה" — הזנת קוד ידנית. ProtectedRoute בלי SubscriptionGate:
                    מי שמקליד קוד נרשם ועדיין לא מנוי, בדיוק המצב שהפייוול חוסם. הדף
                    מפנה ל-/subscribe כשהמתג כבוי. */}
                <Route path="/redeem-referral" element={<ProtectedRoute><RouteErrorBoundary><RedeemReferral.Component /></RouteErrorBoundary></ProtectedRoute>} />
                {/* הנתונים שלך + היסטוריית מדד — ProtectedRoute only, never
                    SubscriptionGate (decision D2): one historical row means access to
                    her own history regardless of subscription, challenge enrolment or
                    course-only status. This is what lets a lapsed member — and an Apple
                    reviewer on an expired demo account — reach a working screen from
                    the paywall link. The paths come from metricHistoryRoutes so the six
                    places that navigate here cannot drift away from the routes. */}
                <Route path={PERSONAL_PLAN_PATH} element={<ProtectedRoute><RouteErrorBoundary><PersonalPlanPage.Component /></RouteErrorBoundary></ProtectedRoute>} />
                <Route path={PROGRESS_PATH} element={<ProtectedRoute><RouteErrorBoundary><ProgressPage.Component /></RouteErrorBoundary></ProtectedRoute>} />
                <Route path={METRIC_HISTORY_PATH} element={<ProtectedRoute><RouteErrorBoundary><MetricHistoryPage.Component /></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/events/:eventId" element={<ProtectedRoute><RouteErrorBoundary><EventDetails.Component /></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/recipe/:id" element={<ProtectedRoute guestAllowed><RouteErrorBoundary><Recipe.Component /></RouteErrorBoundary></ProtectedRoute>} />
              </Route>

              {/* נתיבים מוגנים עם Layout — Header/BottomNav לא נופלים בין מסכים.
                  Layout + Suspense יושבים על parent יחיד; ProtectedRoute עובר ל-element
                  של כל ילד כדי שגם /library ו-/events (guestAllowed) יחלקו את אותו Layout. */}
              <Route element={
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <Outlet />
                  </Suspense>
                </Layout>
              }>
                <Route path="/" element={<ProtectedRoute><RouteErrorBoundary><Home.Component /></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/feed" element={<ProtectedRoute><RouteErrorBoundary><Index.Component /></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/chat" element={<ProtectedRoute><RouteErrorBoundary><Chat.Component /></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><RouteErrorBoundary><Profile.Component /></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute><RouteErrorBoundary><Settings.Component /></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/moderation" element={<AdminRoute><RouteErrorBoundary><Moderation.Component /></RouteErrorBoundary></AdminRoute>} />
                <Route path="/analytics" element={<AdminRoute><RouteErrorBoundary><AnalyticsPage.Component /></RouteErrorBoundary></AdminRoute>} />
                <Route path="/challenge/:challengeId/day/:dayNumber" element={<ProtectedRoute><RouteErrorBoundary><ChallengeAccessGate><ChallengeVideo.Component /></ChallengeAccessGate></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/library" element={<ProtectedRoute guestAllowed><RouteErrorBoundary><Library.Component /></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/events" element={<ProtectedRoute guestAllowed><RouteErrorBoundary><Events.Component /></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/admin/audience-management" element={<AdminRoute><RouteErrorBoundary><AudienceManagement.Component /></RouteErrorBoundary></AdminRoute>} />
                <Route path="/admin/posts-management" element={<AdminRoute><RouteErrorBoundary><PostsManagement.Component /></RouteErrorBoundary></AdminRoute>} />
                <Route path="/admin/onboarding-management" element={<AdminRoute><RouteErrorBoundary><OnboardingManagement.Component /></RouteErrorBoundary></AdminRoute>} />
                <Route path="/admin/funnel-management" element={<AdminRoute><RouteErrorBoundary><FunnelManagement.Component /></RouteErrorBoundary></AdminRoute>} />
                <Route path="/admin/course-products" element={<AdminRoute><RouteErrorBoundary><CourseProductsManagement.Component /></RouteErrorBoundary></AdminRoute>} />
                <Route path="/admin/challenge-management" element={<AdminRoute><RouteErrorBoundary><ChallengeManagement.Component /></RouteErrorBoundary></AdminRoute>} />
                <Route path="/admin/challenge-management/:challengeId" element={<AdminRoute><RouteErrorBoundary><ChallengeEditor.Component /></RouteErrorBoundary></AdminRoute>} />
                <Route path="/admin/course/:id/edit" element={<AdminRoute><RouteErrorBoundary><CourseEditorPage.Component /></RouteErrorBoundary></AdminRoute>} />
                <Route path="/admin/fasting-notifications" element={<AdminRoute><RouteErrorBoundary><FastingNotificationsManagement.Component /></RouteErrorBoundary></AdminRoute>} />
                <Route path="/admin/notifications" element={<AdminRoute><RouteErrorBoundary><NotificationsManagement.Component /></RouteErrorBoundary></AdminRoute>} />
                <Route path="/admin/subscription-banners" element={<AdminRoute><RouteErrorBoundary><SubscriptionBannersManager.Component /></RouteErrorBoundary></AdminRoute>} />
                <Route path="/admin/entry-popups" element={<AdminRoute><RouteErrorBoundary><AdminEntryPopups.Component /></RouteErrorBoundary></AdminRoute>} />

              </Route>

              <Route path="*" element={
                <Suspense fallback={<PageLoader />}>
                  <NotFoundPage.Component />
                </Suspense>
              } />
            </Routes>

                  </StepsProvider>
                </WaterTrackingProvider>
              </FastingProvider>
            </ChallengeProvider>
            </ImpersonationProvider>
          </AuthProvider>
          </PostHogProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
  );
};

export default App;
